import React from 'react'
import { Button, Row, Skeleton } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { trackLink } from '@fs/zion-analytics'
import { baseUrl } from '@fs/zion-config'
import { useFeatureFlag } from '@fs/zion-flags'
import { useAdobeTarget } from '@fs/zion-adobe-target'
import { isTranslationAvailable } from '@fs/zion-locale'

const hasWindow = typeof window !== 'undefined'
const POSITION_EXPERIENCES = {
  CREATE_ACCOUNT_RIGHT: 'HomeHeaderCreateAccountRight',
  CREATE_ACCOUNT_LEFT: 'HomeHeaderCreateAccountLeft',
}
export const homeHeaderPositionActivityConfig = {
  location: 'home_header_position',
  experienceIds: Object.values(POSITION_EXPERIENCES),
}

const handleSignIn = (external) => {
  trackLink(null, '/auth/familysearch/login', 'click_action', 'lo_hdr19_sign_in')
  if (hasWindow) {
    window.location.href = `${external ? baseUrl : ''}/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location.href)}`
  }
}

const SignInButton = ({ gray, external, emphasis }) => {
  const { t } = useTranslation()

  return (
    <Button
      emphasis={emphasis ? 'medium' : undefined}
      id="signInLink"
      onClick={() => handleSignIn(external)}
      color={gray ? 'gray' : undefined}
    >
      {t('common-ui:sign-in.action', 'Sign In')}
    </Button>
  )
}

const CreateAccountButton = ({ external, gray, emphasis = true }) => {
  const { t } = useTranslation()
  const { isOn } = useFeatureFlag('home_header_signUpText')
  const redirectFlag = useFeatureFlag('header_create_account_redirect_to_guided_tree')

  const getRedirectUrl = () => {
    if (window.location.href.includes('/global') && redirectFlag.isOn) {
      return `?returnUrl=${encodeURIComponent('/tree/guided')}`
    }
    return ''
  }

  return (
    <Button
      to={`${external ? baseUrl : ''}/identity/signup/${getRedirectUrl()}`}
      external
      emphasis={emphasis ? 'medium' : undefined}
      linkName="lo_hdr19_register"
      data-test-id="hdr_create_account"
      color={gray ? 'gray' : undefined}
    >
      {isOn && isTranslationAvailable('account.sign-up.action')
        ? t('account.sign-up.action', 'Sign Up')
        : t('account.create.action', 'Create Account')}
    </Button>
  )
}

/**
 * Default (non A/B tested) buttons.
 * @param {Object} props component props
 * @param {boolean} props.external use external baseUrl
 * @param {boolean} props.gray use gray color variant
 */
const DefaultButtons = ({ external, gray }) => {
  return (
    <Row>
      <SignInButton external={external} gray={gray} />
      <CreateAccountButton external={external} gray={gray} />
    </Row>
  )
}

/**
 * A/B tested create account button. Calls useAdobeTarget ONLY when rendered.
 * @param {Object} props component props
 * @param {boolean} props.external use external baseUrl
 * @param {boolean} props.gray use gray color variant
 */
const ABTestButtons = ({ external, gray }) => {
  const {
    data: adobePositionData,
    loading: adobePositionLoading,
    error: adobePositionError,
  } = useAdobeTarget(homeHeaderPositionActivityConfig)

  // Only show skeleton while loading and no error
  const waiting = adobePositionLoading && !adobePositionError

  return waiting ? (
    <Row>
      <Skeleton.Button minWidth="88px" />
      <Skeleton.Button minWidth="88px" />
    </Row>
  ) : (
    <Row>
      <SignInButton
        order={adobePositionData?.experienceId === POSITION_EXPERIENCES.CREATE_ACCOUNT_LEFT ? 2 : 1}
        emphasis={adobePositionData?.experienceId === POSITION_EXPERIENCES.CREATE_ACCOUNT_LEFT}
        gray={gray}
        external={external}
      />
      <CreateAccountButton
        order={adobePositionData?.experienceId === POSITION_EXPERIENCES.CREATE_ACCOUNT_LEFT ? 1 : 2}
        emphasis={adobePositionData?.experienceId !== POSITION_EXPERIENCES.CREATE_ACCOUNT_LEFT}
        external={external}
        gray={gray}
      />
    </Row>
  )
}

/**
 * AuthAccount renders sign in and create account related actions.
 * Splits A/B tested logic into separate component so useAdobeTarget is not invoked
 * when the feature flag is off.
 * @param {Object} props component props
 * @param {boolean} props.external use external baseUrl for links
 * @param {boolean} props.gray render gray variant
 * @returns {JSX.Element} component
 */
export default function AuthAccount({ external, gray }) {
  const { isOn: performABTest } = useFeatureFlag('home_header_signUpButtonTargetShortCircuit')

  return performABTest ? (
    <ABTestButtons external={external} gray={gray} />
  ) : (
    <DefaultButtons external={external} gray={gray} />
  )
}
